/**
 * Client ของหน้าจอจัดการ SSO (`/v1/admin/apps`, `/oauth-clients`, `/app-role-assignments`,
 * `/employees/{id}/app-roles`) — D-175
 *
 * หลักการเดียวกับ `lib/rbacAdmin/client.ts`: ฟังก์ชันไม่ throw, payload ผิดรูปคือ
 * `invalid-response`, ไม่เขียนอะไรลง storage (D-049)
 *
 * `client_secret` ถูกคืนใน `clientSecret` ของผลลัพธ์เท่านั้น — ไม่ถูก log, ไม่ถูกเก็บ
 * ที่ไหนใน module นี้ และ parser ของ `OauthClientOut` ไม่อ่านฟิลด์นี้เลย
 */

import {
  type AddSsoAppRoleBody,
  type CreateSsoAppBody,
  type CreateSsoClientBody,
  type SsoApp,
  type SsoAppRole,
  type SsoAppRoleAssignment,
  type SsoIssuedClient,
  type SsoOauthClient,
  type UpdateSsoClientBody,
} from './types';
import { apiFetch } from '../auth/authorizedFetch';
import { buildApiUrl, getConfiguredApiBaseUrl } from '../apiBase';
import type { ApiErrorEnvelope, RbacAdminFailure } from '../rbacAdmin/client';

export type SsoAdminFailure = RbacAdminFailure;

export const SSO_APP_ROLES_PATH = '/v1/admin/app-roles';
export const SSO_APPS_PATH = '/v1/admin/apps';
export const SSO_CLIENTS_PATH = '/v1/admin/oauth-clients';
export const SSO_ASSIGNMENTS_PATH = '/v1/admin/app-role-assignments';

/* `encodeURIComponent` ทุก segment: รหัสเหล่านี้เป็น business key ที่ผู้ดูแลพิมพ์เอง */
export function buildAppRolesPath(appCode: string): string {
  return `${SSO_APPS_PATH}/${encodeURIComponent(appCode)}/roles`;
}

export function buildClientPath(clientId: string): string {
  return `${SSO_CLIENTS_PATH}/${encodeURIComponent(clientId)}`;
}

export function buildRotateSecretPath(clientId: string): string {
  return `${buildClientPath(clientId)}/rotate-secret`;
}

export function buildEmployeeAppRolesPath(employeeId: string): string {
  return `/v1/admin/employees/${encodeURIComponent(employeeId)}/app-roles`;
}

export function buildEmployeeAppRolePath(employeeId: string, appCode: string): string {
  return `${buildEmployeeAppRolesPath(employeeId)}/${encodeURIComponent(appCode)}`;
}

export function buildEmployeeAppRoleRevokePath(employeeId: string): string {
  return `${buildEmployeeAppRolesPath(employeeId)}/revoke`;
}

export interface AssignmentFilter {
  readonly appCode?: string;
  readonly employeeId?: string;
}

export function buildAssignmentsQuery(filter: AssignmentFilter): string {
  const params = new URLSearchParams();
  if (filter.appCode !== undefined && filter.appCode !== '') {
    params.append('app_code', filter.appCode);
  }
  if (filter.employeeId !== undefined && filter.employeeId !== '') {
    params.append('employee_id', filter.employeeId);
  }
  const text = params.toString();
  return text === '' ? '' : `?${text}`;
}

export interface SsoAdminRequestOptions {
  readonly apiBaseUrl?: string;
  readonly signal?: AbortSignal;
  readonly fetchImpl?: typeof fetch;
}

export type SsoAppListResult =
  | { readonly status: 'ready'; readonly items: readonly SsoApp[] }
  | SsoAdminFailure;
export type SsoAppResult =
  | { readonly status: 'ready'; readonly app: SsoApp }
  | SsoAdminFailure;
export type SsoClientListResult =
  | { readonly status: 'ready'; readonly items: readonly SsoOauthClient[] }
  | SsoAdminFailure;
export type SsoClientResult =
  | { readonly status: 'ready'; readonly client: SsoOauthClient }
  | SsoAdminFailure;
export type SsoIssuedClientResult =
  | ({ readonly status: 'ready' } & SsoIssuedClient)
  | SsoAdminFailure;
export type SsoAssignmentListResult =
  | { readonly status: 'ready'; readonly items: readonly SsoAppRoleAssignment[] }
  | SsoAdminFailure;
export type SsoAssignmentResult =
  | { readonly status: 'ready'; readonly assignment: SsoAppRoleAssignment }
  | SsoAdminFailure;
export type SsoRevokeResult = { readonly status: 'ready' } | SsoAdminFailure;

/* -------------------------------------------------------------- parsing -- */

function asRecord(raw: unknown): Record<string, unknown> | null {
  return typeof raw === 'object' && raw !== null && !Array.isArray(raw)
    ? (raw as Record<string, unknown>)
    : null;
}

function str(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

function bool(value: unknown): boolean | null {
  return typeof value === 'boolean' ? value : null;
}

function strings(value: unknown): string[] | null {
  if (!Array.isArray(value)) {
    return null;
  }
  const out: string[] = [];
  for (const entry of value) {
    const parsed = str(entry);
    if (parsed === null) {
      return null;
    }
    out.push(parsed);
  }
  return out;
}

function parseRole(raw: unknown): SsoAppRole | null {
  const r = asRecord(raw);
  if (r === null) {
    return null;
  }
  const roleCode = str(r.role_code);
  const description = typeof r.description === 'string' ? r.description : null;
  const active = bool(r.active);
  if (roleCode === null || description === null || active === null) {
    return null;
  }
  return { role_code: roleCode, description, active };
}

export function parseSsoApp(raw: unknown): SsoApp | null {
  const r = asRecord(raw);
  if (r === null || !Array.isArray(r.roles)) {
    return null;
  }
  const appCode = str(r.app_code);
  const appName = str(r.app_name);
  const active = bool(r.active);
  if (appCode === null || appName === null || active === null) {
    return null;
  }
  const roles: SsoAppRole[] = [];
  for (const entry of r.roles) {
    const role = parseRole(entry);
    if (role === null) {
      return null;
    }
    roles.push(role);
  }
  return { app_code: appCode, app_name: appName, active, roles };
}

function parseItems<T>(raw: unknown, parseOne: (entry: unknown) => T | null): T[] | null {
  const r = asRecord(raw);
  if (r === null || !Array.isArray(r.items)) {
    return null;
  }
  const items: T[] = [];
  for (const entry of r.items) {
    const parsed = parseOne(entry);
    if (parsed === null) {
      return null;
    }
    items.push(parsed);
  }
  return items;
}

export function parseSsoAppList(raw: unknown): SsoApp[] | null {
  return parseItems(raw, parseSsoApp);
}

export function parseSsoClient(raw: unknown): SsoOauthClient | null {
  const r = asRecord(raw);
  if (r === null) {
    return null;
  }
  const clientId = str(r.client_id);
  const appCode = str(r.app_code);
  const clientName = str(r.client_name);
  const redirectUris = strings(r.redirect_uris);
  const postLogout = strings(r.post_logout_redirect_uris);
  const mfaRoles = strings(r.mfa_roles);
  const refreshEnabled = bool(r.refresh_enabled);
  const allowInsecure = bool(r.allow_insecure_localhost);
  const requireAppRole = bool(r.require_app_role);
  const requireNonce = bool(r.require_nonce);
  const active = bool(r.active);
  const createdBy = str(r.created_by_employee_id);
  const createdAt = str(r.created_at);
  const rotated =
    r.secret_rotated_at === null || r.secret_rotated_at === undefined
      ? null
      : str(r.secret_rotated_at);
  if (
    clientId === null ||
    appCode === null ||
    clientName === null ||
    redirectUris === null ||
    postLogout === null ||
    mfaRoles === null ||
    refreshEnabled === null ||
    allowInsecure === null ||
    requireAppRole === null ||
    requireNonce === null ||
    active === null ||
    createdBy === null ||
    createdAt === null ||
    (r.secret_rotated_at !== null && r.secret_rotated_at !== undefined && rotated === null)
  ) {
    return null;
  }
  return {
    client_id: clientId,
    app_code: appCode,
    client_name: clientName,
    redirect_uris: redirectUris,
    post_logout_redirect_uris: postLogout,
    mfa_roles: mfaRoles,
    refresh_enabled: refreshEnabled,
    allow_insecure_localhost: allowInsecure,
    require_app_role: requireAppRole,
    require_nonce: requireNonce,
    active,
    secret_rotated_at: rotated,
    created_by_employee_id: createdBy,
    created_at: createdAt,
  };
}

export function parseSsoClientList(raw: unknown): SsoOauthClient[] | null {
  return parseItems(raw, parseSsoClient);
}

export function parseSsoIssuedClient(raw: unknown): SsoIssuedClient | null {
  const r = asRecord(raw);
  const client = parseSsoClient(raw);
  const secret = r === null ? null : str(r.client_secret);
  return client === null || secret === null ? null : { client, clientSecret: secret };
}

export function parseSsoAssignment(raw: unknown): SsoAppRoleAssignment | null {
  const r = asRecord(raw);
  if (r === null) {
    return null;
  }
  const employeeId = str(r.employee_id);
  const appCode = str(r.app_code);
  const roleCode = str(r.role_code);
  const grantedBy = str(r.granted_by_employee_id);
  const grantedAt = str(r.granted_at);
  const selfGranted = bool(r.self_granted);
  const displayName =
    r.display_name === null || r.display_name === undefined ? null : str(r.display_name);
  if (
    employeeId === null ||
    appCode === null ||
    roleCode === null ||
    grantedBy === null ||
    grantedAt === null ||
    selfGranted === null ||
    (r.display_name !== null && r.display_name !== undefined && displayName === null)
  ) {
    return null;
  }
  return {
    employee_id: employeeId,
    display_name: displayName,
    app_code: appCode,
    role_code: roleCode,
    granted_by_employee_id: grantedBy,
    granted_at: grantedAt,
    self_granted: selfGranted,
  };
}

export function parseSsoAssignmentList(raw: unknown): SsoAppRoleAssignment[] | null {
  return parseItems(raw, parseSsoAssignment);
}

function parseErrorEnvelope(raw: unknown): ApiErrorEnvelope {
  const r = asRecord(raw);
  if (r === null) {
    return { error_code: null, message: null, rule: null, details: {} };
  }
  const details = asRecord(r.details) ?? {};
  return {
    error_code: str(r.error_code),
    message: str(r.message),
    rule: str(details.rule),
    details,
  };
}

/* ------------------------------------------------------------- requests -- */

async function readEnvelope(response: Response): Promise<ApiErrorEnvelope> {
  try {
    return parseErrorEnvelope(await response.json());
  } catch {
    return parseErrorEnvelope(null);
  }
}

type PayloadResult =
  | { readonly status: 'payload'; readonly payload: unknown }
  | SsoAdminFailure;

async function requestPayload(
  path: string,
  init: RequestInit,
  options: SsoAdminRequestOptions,
  expectBody = true,
): Promise<PayloadResult> {
  const fetchImpl = options.fetchImpl ?? apiFetch;
  if (typeof fetchImpl !== 'function') {
    return { status: 'network-error', detail: 'fetch ไม่พร้อมใช้งานใน runtime นี้' };
  }

  const url = buildApiUrl(options.apiBaseUrl ?? getConfiguredApiBaseUrl(), path);

  let response: Response;
  try {
    response = await fetchImpl(url, {
      credentials: 'same-origin',
      cache: 'no-store',
      ...init,
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (error) {
    if (options.signal?.aborted || (error as Error | undefined)?.name === 'AbortError') {
      return { status: 'cancelled' };
    }
    return {
      status: 'network-error',
      detail: error instanceof Error ? error.message : 'ไม่ทราบสาเหตุ',
    };
  }

  if (options.signal?.aborted) {
    return { status: 'cancelled' };
  }

  switch (response.status) {
    case 400:
      return { status: 'bad-request', error: await readEnvelope(response) };
    case 401:
      return { status: 'unauthenticated' };
    case 403:
      return { status: 'forbidden', error: await readEnvelope(response) };
    case 404:
      return { status: 'not-found', error: await readEnvelope(response) };
    case 409:
      return { status: 'conflict', error: await readEnvelope(response) };
    case 422:
      return { status: 'rule-violation', error: await readEnvelope(response) };
    default:
      break;
  }

  if (!response.ok) {
    return { status: 'server-error', httpStatus: response.status };
  }
  if (!expectBody) {
    return { status: 'payload', payload: null };
  }

  try {
    return { status: 'payload', payload: await response.json() };
  } catch {
    return { status: 'invalid-response' };
  }
}

const READ_INIT: RequestInit = { method: 'GET', headers: { Accept: 'application/json' } };

function jsonInit(method: string, body: unknown): RequestInit {
  return {
    method,
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  };
}

async function readList<T>(
  path: string,
  parse: (raw: unknown) => T[] | null,
  options: SsoAdminRequestOptions,
): Promise<{ readonly status: 'ready'; readonly items: readonly T[] } | SsoAdminFailure> {
  const result = await requestPayload(path, READ_INIT, options);
  if (result.status !== 'payload') {
    return result;
  }
  const items = parse(result.payload);
  return items === null ? { status: 'invalid-response' } : { status: 'ready', items };
}

/* --- serializers: คีย์ตรงตาม schema (`extra = "forbid"`) ไม่ spread object ทั้งก้อน --- */

export function serializeCreateAppBody(body: CreateSsoAppBody): Record<string, unknown> {
  return {
    app_code: body.app_code,
    app_name: body.app_name,
    roles: body.roles.map((role) => ({
      role_code: role.role_code,
      description: role.description,
    })),
  };
}

export function serializeAddAppRoleBody(body: AddSsoAppRoleBody): Record<string, unknown> {
  return { role_code: body.role_code, description: body.description };
}

export function serializeCreateClientBody(body: CreateSsoClientBody): Record<string, unknown> {
  return {
    client_id: body.client_id,
    app_code: body.app_code,
    client_name: body.client_name,
    redirect_uris: [...body.redirect_uris],
    post_logout_redirect_uris: [...body.post_logout_redirect_uris],
    mfa_roles: [...body.mfa_roles],
    refresh_enabled: body.refresh_enabled,
    allow_insecure_localhost: body.allow_insecure_localhost,
  };
}

/** ส่งเฉพาะฟิลด์ที่ระบุ — ฟิลด์ที่ไม่ส่งคือคงเดิม ลิสต์ที่ส่งแทนที่ทั้งชุด */
export function serializeUpdateClientBody(body: UpdateSsoClientBody): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (body.client_name !== undefined) out.client_name = body.client_name;
  if (body.redirect_uris !== undefined) out.redirect_uris = [...body.redirect_uris];
  if (body.post_logout_redirect_uris !== undefined) {
    out.post_logout_redirect_uris = [...body.post_logout_redirect_uris];
  }
  if (body.mfa_roles !== undefined) out.mfa_roles = [...body.mfa_roles];
  if (body.refresh_enabled !== undefined) out.refresh_enabled = body.refresh_enabled;
  if (body.allow_insecure_localhost !== undefined) {
    out.allow_insecure_localhost = body.allow_insecure_localhost;
  }
  if (body.require_app_role !== undefined) out.require_app_role = body.require_app_role;
  if (body.active !== undefined) out.active = body.active;
  return out;
}

/* ------------------------------------------------------------ endpoints -- */

export async function fetchSsoApps(options: SsoAdminRequestOptions = {}): Promise<SsoAppListResult> {
  return readList(SSO_APP_ROLES_PATH, parseSsoAppList, options);
}

async function appResult(
  path: string,
  init: RequestInit,
  options: SsoAdminRequestOptions,
): Promise<SsoAppResult> {
  const result = await requestPayload(path, init, options);
  if (result.status !== 'payload') {
    return result;
  }
  const app = parseSsoApp(result.payload);
  return app === null ? { status: 'invalid-response' } : { status: 'ready', app };
}

export function createSsoApp(
  body: CreateSsoAppBody,
  options: SsoAdminRequestOptions = {},
): Promise<SsoAppResult> {
  return appResult(SSO_APPS_PATH, jsonInit('POST', serializeCreateAppBody(body)), options);
}

export function addSsoAppRole(
  appCode: string,
  body: AddSsoAppRoleBody,
  options: SsoAdminRequestOptions = {},
): Promise<SsoAppResult> {
  return appResult(
    buildAppRolesPath(appCode),
    jsonInit('POST', serializeAddAppRoleBody(body)),
    options,
  );
}

export async function fetchSsoClients(
  options: SsoAdminRequestOptions = {},
): Promise<SsoClientListResult> {
  return readList(SSO_CLIENTS_PATH, parseSsoClientList, options);
}

async function issuedResult(
  path: string,
  init: RequestInit,
  options: SsoAdminRequestOptions,
): Promise<SsoIssuedClientResult> {
  const result = await requestPayload(path, init, options);
  if (result.status !== 'payload') {
    return result;
  }
  const issued = parseSsoIssuedClient(result.payload);
  return issued === null ? { status: 'invalid-response' } : { status: 'ready', ...issued };
}

export function createSsoClient(
  body: CreateSsoClientBody,
  options: SsoAdminRequestOptions = {},
): Promise<SsoIssuedClientResult> {
  return issuedResult(SSO_CLIENTS_PATH, jsonInit('POST', serializeCreateClientBody(body)), options);
}

export async function updateSsoClient(
  clientId: string,
  body: UpdateSsoClientBody,
  options: SsoAdminRequestOptions = {},
): Promise<SsoClientResult> {
  const result = await requestPayload(
    buildClientPath(clientId),
    jsonInit('PATCH', serializeUpdateClientBody(body)),
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }
  const client = parseSsoClient(result.payload);
  return client === null ? { status: 'invalid-response' } : { status: 'ready', client };
}

export function rotateSsoClientSecret(
  clientId: string,
  options: SsoAdminRequestOptions = {},
): Promise<SsoIssuedClientResult> {
  return issuedResult(
    buildRotateSecretPath(clientId),
    { method: 'POST', headers: { Accept: 'application/json' } },
    options,
  );
}

export function fetchSsoAssignments(
  filter: AssignmentFilter = {},
  options: SsoAdminRequestOptions = {},
): Promise<SsoAssignmentListResult> {
  return readList(
    `${SSO_ASSIGNMENTS_PATH}${buildAssignmentsQuery(filter)}`,
    parseSsoAssignmentList,
    options,
  );
}

export function fetchEmployeeSsoRoles(
  employeeId: string,
  options: SsoAdminRequestOptions = {},
): Promise<SsoAssignmentListResult> {
  return readList(buildEmployeeAppRolesPath(employeeId), parseSsoAssignmentList, options);
}

export async function setEmployeeSsoRole(
  employeeId: string,
  appCode: string,
  roleCode: string,
  options: SsoAdminRequestOptions = {},
): Promise<SsoAssignmentResult> {
  const result = await requestPayload(
    buildEmployeeAppRolePath(employeeId, appCode),
    jsonInit('PUT', { role_code: roleCode }),
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }
  const assignment = parseSsoAssignment(result.payload);
  return assignment === null ? { status: 'invalid-response' } : { status: 'ready', assignment };
}

export async function revokeEmployeeSsoRole(
  employeeId: string,
  appCode: string,
  options: SsoAdminRequestOptions = {},
): Promise<SsoRevokeResult> {
  const result = await requestPayload(
    buildEmployeeAppRoleRevokePath(employeeId),
    jsonInit('POST', { app_code: appCode }),
    options,
    false,
  );
  return result.status === 'payload' ? { status: 'ready' } : result;
}
